import { MarcaConfig, MarcaConfigAPI } from './types';

/**
 * Mapea la configuración de marca desde el formato del API (PascalCase)
 * al formato usado internamente en el código (camelCase)
 * 
 * API v8: Monedas y Financiamiento ahora vienen en payment-summary, no aquí
 */
export const mapApiConfigToMarcaConfig = (apiConfig: MarcaConfigAPI): MarcaConfig => {
  return {
    // Campos básicos de marca
    idMarca: apiConfig.IdMarca,
    idCanal: (apiConfig as any).IdCanal,
    nombre: apiConfig.Nombre || (apiConfig as any).NombreMarca,
    logo: apiConfig.Logo || (apiConfig as any).LogoMarca,
    logoDark: apiConfig.LogoDark || (apiConfig as any).LogoMarcaDark || undefined,
    icono: apiConfig.Icono || (apiConfig as any).IconoMarca,
    colorBoton: apiConfig.ColorBoton,
    colorBg: apiConfig.ColorBg,
    idioma: apiConfig.Idioma,
    faq: apiConfig.FAQ,
    asistenteIA: apiConfig.AsistenteIA ?? false,
    
    // Campos de configuración adicional
    redesSociales: apiConfig.RedesSociales || [],
    terminosCondiciones: apiConfig.TerminosCondiciones || null,
    portalPagos: apiConfig.PortalPagos ?? true,
    mantenimiento: apiConfig.Mantenimiento ?? false,
    
    // Google Analytics 4 Measurement ID
    ga4MeasurementId: apiConfig.GoogleAnalyticsId || null,
    
    // Google Tag Manager Container ID
    googleTag: apiConfig.GoogleTag || null,
    
    // REMOVIDOS en API v8 (ahora vienen en payment-summary.Marca):
    // - monedadefault → Marca.Monedas[].Predeterminado
    // - monedas → Marca.Monedas
    // - financiamiento → Marca.Productos.Financiamiento
  };
};

// API v8: Las funciones getAvailableCurrencies y getDefaultCurrency han sido removidas
// Las monedas ahora vienen en payment-summary.Marca.Monedas
// La moneda por defecto se identifica con el campo Predeterminado: true

/**
 * Obtiene el idioma por defecto desde la configuración global
 * Fallback a 'es' si no está configurado
 */
export const getDefaultLanguage = (): string => {
  const config = (window as any).PAGOS_CONFIG;
  return config?.marca?.idioma || 'es';
};

/**
 * Obtiene y parsea las FAQs desde la configuración global
 * Retorna array vacío si no hay FAQs o hay error al parsear
 */
export const getFAQs = (): Array<{ question: string; answer: string }> => {
  try {
    const config = (window as any).PAGOS_CONFIG;
    const faqString = config?.marca?.faq;
    if (faqString && typeof faqString === 'string') {
      return JSON.parse(faqString);
    }
    return [];
  } catch (error) {
    console.error('Error parsing FAQ JSON:', error);
    return [];
  }
};

/**
 * Verifica si existe un logo configurado para dark mode
 */
export const hasDarkModeLogo = (): boolean => {
  const config = (window as any).PAGOS_CONFIG;
  const logoDark = config?.marca?.logoDark;
  return logoDark !== null && logoDark !== undefined && logoDark !== '';
};

/**
 * Obtiene la URL base del API desde la configuración global
 */
export const getApiBaseUrl = (): string => {
  const config = (window as any).PAGOS_CONFIG;
  return config?.marca?.ApiBaseUrl || config?.apiBaseUrl || '';
};

/**
 * Extrae el brand keyword del pathname de la URL
 * Ejemplo: /coca-cola-femsa → "coca-cola-femsa"
 * Ejemplo: / → "acme" (default)
 */
export const getBrandKeywordFromUrl = (): string => {
  const pathname = window.location.pathname;
  // Extraer primer segmento del path, excluyendo segmentos vacíos
  const segments = pathname.split('/').filter(Boolean);
  return segments.length > 0 ? segments[0] : 'acme';
};
